/**
 * Client-side image downscaler for feedback attachments.
 *
 * Target: produce a JPEG <= 1 MB while preserving aspect ratio and visible
 * detail. Strategy — start at max edge 1920 and quality 0.85; drop quality
 * first, then shrink dimensions, retrying until we fit or give up.
 *
 * Always re-encodes to JPEG regardless of input format. Simpler and yields
 * predictable sizes. HEIC/HEIF from Safari is decoded automatically by the
 * browser when we draw it to canvas.
 */

const TARGET_MAX_BYTES = 1_000_000; // 1 MB
const INITIAL_MAX_EDGE = 1920;
const MIN_MAX_EDGE = 480;          // don't shrink below this — not useful to debug from
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.4;
const MAX_ATTEMPTS = 8;

export interface DownscaledImage {
    /** Base64-encoded JPEG bytes, without `data:…;base64,` prefix. */
    base64: string;
    contentType: 'image/jpeg';
    byteLength: number;
    width: number;
    height: number;
    /** Object URL for preview — caller MUST URL.revokeObjectURL when the preview is unmounted. */
    previewUrl: string;
}

export class DownscaleError extends Error {
    readonly cause?: unknown;
    constructor(message: string, cause?: unknown) {
        super(message);
        this.name = 'DownscaleError';
        this.cause = cause;
    }
}

/**
 * Downscale and re-encode `file` to JPEG under 1 MB.
 * Throws `DownscaleError` if the image can't be loaded or cannot be squeezed
 * under the limit (should be vanishingly rare for typical camera/phone photos).
 */
export async function downscaleImageForUpload(file: File): Promise<DownscaledImage> {
    if (!file.type.startsWith('image/')) {
        throw new DownscaleError(`Not an image: ${file.type || 'unknown type'}`);
    }
    const img = await loadImage(file);
    try {
        let maxEdge = Math.min(Math.max(img.naturalWidth, img.naturalHeight), INITIAL_MAX_EDGE);
        let quality = INITIAL_QUALITY;

        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, maxEdge);
            const blob = await drawToJpeg(img, width, height, quality);
            if (blob.size <= TARGET_MAX_BYTES) {
                const base64 = await blobToBase64(blob);
                return {
                    base64,
                    contentType: 'image/jpeg',
                    byteLength: blob.size,
                    width,
                    height,
                    previewUrl: URL.createObjectURL(blob),
                };
            }
            // First exhaust quality steps, then shrink dimensions.
            if (quality > MIN_QUALITY) {
                quality = Math.max(MIN_QUALITY, quality - 0.15);
            } else if (maxEdge > MIN_MAX_EDGE) {
                maxEdge = Math.max(MIN_MAX_EDGE, Math.round(maxEdge * 0.8));
                quality = INITIAL_QUALITY;   // reset quality after a dimension drop — better perceived quality
            } else {
                break;
            }
        }
        throw new DownscaleError('Could not compress image under 1 MB');
    } finally {
        // The <img> element is detached implicitly; no resource leak.
    }
}

// ── internals ────────────────────────────────────────────────────────

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(new DownscaleError('Browser could not decode image', e));
        };
        img.src = url;
    });
}

function fitWithin(w: number, h: number, maxEdge: number): { width: number; height: number } {
    if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
    if (w >= h) {
        return { width: maxEdge, height: Math.max(1, Math.round((h / w) * maxEdge)) };
    }
    return { width: Math.max(1, Math.round((w / h) * maxEdge)), height: maxEdge };
}

function drawToJpeg(img: HTMLImageElement, w: number, h: number, quality: number): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new DownscaleError('Canvas 2d context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new DownscaleError('canvas.toBlob returned null'))),
            'image/jpeg',
            quality,
        );
    });
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(new DownscaleError('FileReader failed', reader.error));
        reader.readAsDataURL(blob);
    });
}
